import React, { useState, useEffect, useRef, useCallback } from "react";
import { hermesApi } from "../../lib/api";
import { toast } from "sonner";
import {
  PaperPlaneRight, Plus, Trash, PencilSimple, Check, X,
  Lightning, User, Paperclip, Microphone, MicrophoneSlash,
  CircleNotch, DotsThree, ChatCircle,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import ReactMarkdown from "react-markdown";

// ── Markdown renderer ─────────────────────────────────────────────────────────
function HermesMarkdown({ content }) {
  return (
    <ReactMarkdown
      className="prose prose-sm max-w-none prose-slate
        prose-headings:font-semibold prose-headings:text-slate-800
        prose-p:text-slate-700 prose-p:leading-relaxed
        prose-strong:text-slate-800
        prose-code:text-indigo-700 prose-code:bg-indigo-50 prose-code:px-1 prose-code:rounded prose-code:text-[12px]
        prose-pre:bg-slate-900 prose-pre:text-slate-100
        prose-ul:text-slate-700 prose-ol:text-slate-700
        prose-li:marker:text-slate-400
        prose-blockquote:border-l-indigo-300 prose-blockquote:text-slate-600"
    >
      {content}
    </ReactMarkdown>
  );
}

// ── Single message bubble ─────────────────────────────────────────────────────
function Message({ msg }) {
  const isHermes  = msg.role === "hermes";
  const isPending = msg.pending;

  return (
    <div className={`flex gap-4 max-w-3xl ${isHermes ? "mx-auto w-full" : "mx-auto w-full justify-end"}`}>
      {isHermes && (
        <div className="w-8 h-8 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center shrink-0 mt-0.5">
          <Lightning size={14} weight="fill" className="text-indigo-600" />
        </div>
      )}

      <div className={`flex-1 min-w-0 ${isHermes ? "" : "flex justify-end"}`}>
        <div className={`inline-block ${isHermes ? "w-full" : "max-w-[85%]"}`}>
          <div className={`text-[11px] font-semibold mb-1.5 ${isHermes ? "text-indigo-600" : "text-slate-400 text-right"}`}>
            {isHermes ? "Hermes" : "You"}
            {!isPending && (
              <span className="font-mono font-normal ml-2 text-slate-400">
                {new Date(msg.created_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div className={`rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${
            isHermes
              ? "bg-white border border-slate-200 text-slate-800 shadow-sm"
              : "bg-slate-900 text-white"
          } ${isPending ? "opacity-60" : ""}`}>
            {isPending ? (
              <span className="flex items-center gap-2 text-slate-400 text-[13px]">
                <CircleNotch size={13} className="animate-spin" />
                Hermes is on it — processing your request…
              </span>
            ) : isHermes ? (
              <HermesMarkdown content={msg.content} />
            ) : (
              <p className="whitespace-pre-wrap text-[14px]">{msg.content}</p>
            )}
          </div>
        </div>
      </div>

      {!isHermes && (
        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
          <User size={14} weight="fill" className="text-slate-600" />
        </div>
      )}
    </div>
  );
}

// ── Conversation list item ────────────────────────────────────────────────────
function ConvItem({ conv, isActive, onSelect, onRename, onDelete }) {
  const [editing, setEditing]   = useState(false);
  const [draft,   setDraft]     = useState(conv.title);
  const inputRef                = useRef(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(conv.title);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const commitEdit = () => {
    if (draft.trim() && draft.trim() !== conv.title) {
      onRename(conv.id, draft.trim());
    }
    setEditing(false);
  };

  return (
    <div
      onClick={() => !editing && onSelect(conv.id)}
      className={`group relative flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
        isActive
          ? "bg-white/[0.12] text-white"
          : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-200"
      }`}
    >
      <ChatCircle size={14} className="shrink-0 mt-0.5" weight={isActive ? "fill" : "regular"} />

      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditing(false);
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white/10 text-white text-[12px] rounded px-1 py-0.5 outline-none border border-indigo-400/50"
          />
        ) : (
          <div className="text-[12px] font-medium truncate leading-snug">{conv.title}</div>
        )}
        {!editing && (
          <div className="text-[10px] text-slate-500 truncate mt-0.5 font-mono">
            {conv.message_count || 0} messages
          </div>
        )}
      </div>

      {!editing && (
        <div className="opacity-0 group-hover:opacity-100 shrink-0 flex gap-0.5 transition-opacity">
          <button
            onClick={startEdit}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-white/10 text-slate-400 hover:text-white"
          >
            <PencilSimple size={11} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400"
          >
            <Trash size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Attachment pill ───────────────────────────────────────────────────────────
function AttachPill({ name, onRemove }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-[12px] text-slate-700">
      <Paperclip size={11} className="text-slate-400" />
      <span className="max-w-[120px] truncate">{name}</span>
      <button onClick={onRemove} className="text-slate-400 hover:text-slate-700 ml-0.5">
        <X size={11} />
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HermesChatPage() {
  const [conversations, setConversations] = useState([]);
  const [activeId,      setActiveId]      = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState("");
  const [sending,       setSending]       = useState(false);
  const [loadingMsgs,   setLoadingMsgs]   = useState(false);
  const [attachments,   setAttachments]   = useState([]); // [{name, text}]
  const [listening,     setListening]     = useState(false);
  const [sidebarOpen,   setSidebarOpen]   = useState(true);

  const bottomRef    = useRef(null);
  const textareaRef  = useRef(null);
  const fileInputRef = useRef(null);
  const recognRef    = useRef(null);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await hermesApi.conversations.list();
      setConversations(data);
      return data;
    } catch { return []; }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Load messages for active conversation
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    setLoadingMsgs(true);
    hermesApi.conversations.messages(activeId)
      .then(({ data }) => setMessages(data))
      .catch(() => toast.error("Failed to load messages"))
      .finally(() => setLoadingMsgs(false));
  }, [activeId]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Create new conversation
  const newConversation = async () => {
    try {
      const { data } = await hermesApi.conversations.create({ title: "New Chat" });
      setConversations((prev) => [data, ...prev]);
      setActiveId(data.id);
      setMessages([]);
      textareaRef.current?.focus();
    } catch { toast.error("Failed to create conversation"); }
  };

  // Select conversation
  const selectConversation = (id) => {
    if (id !== activeId) setActiveId(id);
  };

  // Rename conversation
  const renameConversation = async (id, title) => {
    try {
      const { data } = await hermesApi.conversations.rename(id, { title });
      setConversations((prev) => prev.map((c) => c.id === id ? { ...c, title: data.title } : c));
    } catch { toast.error("Failed to rename"); }
  };

  // Delete conversation
  const deleteConversation = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await hermesApi.conversations.delete(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch { toast.error("Failed to delete"); }
  };

  // Poll for Hermes reply after sending
  const pollForReply = useCallback(async (cid, afterMsgId, attempts = 0) => {
    if (attempts > 40) return; // give up after ~2 minutes
    try {
      const { data } = await hermesApi.conversations.messages(cid);
      const hasReply = data.some((m) => m.role === "hermes" && m.id !== afterMsgId &&
        new Date(m.created_at) > new Date(Date.now() - 300000)); // within last 5 min
      if (hasReply) {
        setMessages(data);
        await loadConversations();
        return;
      }
    } catch { }
    setTimeout(() => pollForReply(cid, afterMsgId, attempts + 1), 3000);
  }, [loadConversations]);

  // Send message
  const send = async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || sending) return;

    let cid = activeId;

    // Auto-create conversation if none selected
    if (!cid) {
      try {
        const title = text.slice(0, 60) || "New Chat";
        const { data } = await hermesApi.conversations.create({ title });
        cid = data.id;
        setConversations((prev) => [data, ...prev]);
        setActiveId(cid);
      } catch {
        toast.error("Failed to create conversation");
        return;
      }
    }

    setSending(true);
    setInput("");
    const sentAttachments = [...attachments];
    setAttachments([]);

    // Optimistic user bubble + pending Hermes bubble
    const optId = `opt-u-${Date.now()}`;
    const pendId = `opt-h-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: optId, role: "operator", content: text, created_at: new Date().toISOString() },
      { id: pendId, role: "hermes", content: "", pending: true, created_at: new Date().toISOString() },
    ]);

    try {
      const { data } = await hermesApi.conversations.send(cid, {
        content: text,
        attachments: sentAttachments,
      });

      // Replace optimistic user bubble with real message, keep pending Hermes bubble
      setMessages((m) => [
        ...m.filter((x) => x.id !== optId && x.id !== pendId),
        data.user_message,
        { id: pendId, role: "hermes", content: "", pending: true, created_at: new Date().toISOString() },
      ]);

      // Auto-rename if first message
      const conv = conversations.find((c) => c.id === cid);
      if (conv && conv.title === "New Chat" && text) {
        await renameConversation(cid, text.slice(0, 60));
      }

      // Poll until Hermes replies (it runs its cycle asynchronously)
      pollForReply(cid, data.user_message.id);
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optId && x.id !== pendId));
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  // File attachment
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const text = await file.text().catch(() => `[Could not read ${file.name}]`);
      setAttachments((prev) => [...prev, { name: file.name, text }]);
    }
    e.target.value = "";
  };

  // Voice input
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input not supported in this browser"); return; }

    if (listening) {
      recognRef.current?.stop();
      setListening(false);
      return;
    }

    const recogn = new SR();
    recogn.continuous     = false;
    recogn.interimResults = false;
    recogn.lang           = "en-AU";
    recogn.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput((prev) => (prev ? prev + " " : "") + transcript);
      setListening(false);
    };
    recogn.onerror = () => { toast.error("Voice recognition failed"); setListening(false); };
    recogn.onend   = () => setListening(false);
    recogn.start();
    recognRef.current = recogn;
    setListening(true);
  };

  const activeConv = conversations.find((c) => c.id === activeId);

  return (
    <div className="flex h-full overflow-hidden bg-slate-50">
      {/* ── Conversations sidebar ── */}
      <div className={`shrink-0 flex flex-col bg-slate-950 border-r border-slate-800 transition-all duration-200 ${sidebarOpen ? "w-64" : "w-0 overflow-hidden"}`}>
        <div className="flex items-center justify-between px-3 py-3 border-b border-slate-800 shrink-0">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Chats</span>
          <button
            onClick={newConversation}
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            title="New conversation"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {conversations.length === 0 ? (
            <p className="text-[11px] text-slate-600 text-center py-8 px-3 leading-relaxed">
              No conversations yet.<br />Click + to start one.
            </p>
          ) : (
            conversations.map((c) => (
              <ConvItem
                key={c.id}
                conv={c}
                isActive={c.id === activeId}
                onSelect={selectConversation}
                onRename={renameConversation}
                onDelete={deleteConversation}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Chat pane ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-5 h-12 flex items-center gap-3 shrink-0">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          >
            <ChatCircle size={15} />
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-[13px] font-semibold text-slate-700 truncate">
              {activeConv ? activeConv.title : "Hermes Chat"}
            </span>
          </div>
          {!activeId && (
            <button
              onClick={newConversation}
              className="flex items-center gap-1.5 text-[12px] text-indigo-600 hover:text-indigo-800 font-medium"
            >
              <Plus size={12} /> New Chat
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {!activeId ? (
            <div className="flex flex-col items-center justify-center h-full text-center pb-16">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
                <Lightning size={28} weight="fill" className="text-indigo-500" />
              </div>
              <h2 className="font-display text-[20px] font-bold text-slate-800 mb-2">
                Talk to Hermes
              </h2>
              <p className="text-[13px] text-slate-400 max-w-xs leading-relaxed mb-6">
                Ask for an intelligence briefing, research a topic, or discuss what you've seen in the radar.
              </p>
              <button
                onClick={newConversation}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors"
              >
                <Plus size={14} /> Start a conversation
              </button>
            </div>
          ) : loadingMsgs ? (
            <div className="flex items-center justify-center h-full text-[13px] text-slate-400">
              <CircleNotch size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center pb-16">
              <Lightning size={24} weight="fill" className="text-indigo-200 mb-3" />
              <p className="text-[14px] text-slate-400">Send a message to begin</p>
            </div>
          ) : (
            <div className="space-y-6 max-w-3xl mx-auto">
              {messages.map((msg) => (
                <Message key={msg.id} msg={msg} />
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Compose area */}
        {(activeId || true) && (
          <div className="bg-white border-t border-slate-200 px-4 py-3 shrink-0">
            {/* Attachment pills */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((a, i) => (
                  <AttachPill
                    key={i}
                    name={a.name}
                    onRemove={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}

            <div className="max-w-3xl mx-auto">
              <div className="flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2.5 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
                  }}
                  onKeyDown={handleKey}
                  placeholder={activeId ? "Message Hermes… (⌘+Enter to send)" : "Start a conversation…"}
                  rows={1}
                  className="flex-1 bg-transparent text-[14px] text-slate-800 resize-none focus:outline-none placeholder:text-slate-400 leading-relaxed py-0.5"
                  style={{ minHeight: "24px", maxHeight: "200px" }}
                />
                <div className="flex items-center gap-1 pb-0.5 shrink-0">
                  {/* Attach */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".txt,.md,.csv,.json,.pdf,.js,.py,.ts,.tsx,.jsx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                    title="Attach file"
                  >
                    <Paperclip size={15} />
                  </button>

                  {/* Voice */}
                  <button
                    onClick={toggleVoice}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                      listening
                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                        : "text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                    }`}
                    title={listening ? "Stop recording" : "Voice input"}
                  >
                    {listening ? <MicrophoneSlash size={15} /> : <Microphone size={15} />}
                  </button>

                  {/* Send */}
                  <button
                    onClick={send}
                    disabled={sending || (!input.trim() && attachments.length === 0)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Send (⌘+Enter)"
                  >
                    {sending
                      ? <CircleNotch size={14} className="animate-spin" />
                      : <PaperPlaneRight size={14} weight="fill" />
                    }
                  </button>
                </div>
              </div>

              <p className="text-[11px] text-slate-400 text-center mt-1.5">
                Hermes receives your message and responds on its next cycle (triggered immediately)
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
